import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../stores/authStore';
import { dashboard, simulations as simulationsApi } from '../../services/api';
import { SystemCheckModal, CompetencyReadinessCard } from '../../features/readiness';
import { AdminPageHeader } from '../../features/admin/components/AdminPageHeader';
import { DashboardFAB } from '../../components/ui/DashboardFAB';
import { useTenantBranding } from '../../hooks/useTenantBranding';
import { useTranslation } from '../../i18n';
import { Pagination, usePagination } from '../../components/ui/Pagination';

export interface SimulationItem {
  id: string;
  title: string;
  description: string;
  category?: string;
  difficulty?: string;
  status: string;
  learnerRole?: string;
  targetPersona?: string;
  targetPersonaRole?: string;
  persona?: {
    name?: string;
    role?: string;
    avatarUrl?: string;
    avatarType?: string;
  };
  timeLimitMinutes?: number;
  estimatedMinutes?: number;
  totalDurationMinutes?: number;
  minutesConsumed?: number;
  progressPercentage?: number;
  allowedLanguages?: string[];
  maxAttempts?: number;
  attemptsUsed?: number;
  attemptsRemaining?: number;
  learnerStatus: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';
  availableAction: 'START' | 'RESUME' | 'VIEW_RESULTS' | 'MAX_ATTEMPTS_REACHED' | 'LOCKED_FUTURE' | 'EXPIRED';
  isAvailable: boolean;
  startsAt?: string;
  dueAt?: string;
  activeAttemptId?: string | null;
  targetAttemptId?: string | null;
  isAssigned?: boolean;
  assignment?: any;
  requiresCamera?: boolean;
  requiresMicrophone?: boolean;
  requiresSpeaker?: boolean;
  hardwareRequirements?: {
    requiresCamera?: boolean;
    requiresMicrophone?: boolean;
    requiresSpeaker?: boolean;
  };
}

interface DashboardStats {
  total: number;
  notStarted: number;
  inProgress: number;
  completed: number;
}

// Global cache for instant transition and zero-delay navigation back to dashboard
let cachedSimulations: SimulationItem[] | null = null;
let cachedStats: DashboardStats | null = null;
let cachedUserId: string | null = null;

export function clearDashboardCache() {
  cachedSimulations = null;
  cachedStats = null;
  cachedUserId = null;
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { branding } = useTenantBranding();
  const navigate = useNavigate();

  // Invalidate in-memory cache if switching users or learner identity changed
  if (user?.userId && cachedUserId && cachedUserId !== user.userId) {
    clearDashboardCache();
  }

  const [stats, setStats] = useState<DashboardStats>(
    cachedStats || {
      total: 0,
      notStarted: 0,
      inProgress: 0,
      completed: 0,
    }
  );
  const [allSimulations, setAllSimulations] = useState<SimulationItem[]>(cachedSimulations || []);
  const [isLoading, setIsLoading] = useState<boolean>(!cachedSimulations);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [selectedTab, setSelectedTab] = useState<'ALL' | 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED'>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState<'createdAt' | 'title' | 'timeLimit'>('createdAt');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedSimForPreflight, setSelectedSimForPreflight] = useState<SimulationItem | null>(null);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 200);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Main data loader: fetches comprehensive list once and caches
  const loadDashboardData = useCallback(async (isSilent = false) => {
    if (!isSilent && !cachedSimulations) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const res = await dashboard.get({ limit: 100 });
      if (res.success && res.data) {
        const data = res.data as any;
        const items: SimulationItem[] = data.simulations || [];

        const computedStats: DashboardStats = data.stats || {
          total: data.counts?.all ?? items.length,
          notStarted: data.counts?.notStarted ?? items.filter((s) => s.learnerStatus === 'NOT_STARTED').length,
          inProgress: data.counts?.inProgress ?? items.filter((s) => s.learnerStatus === 'IN_PROGRESS').length,
          completed: data.counts?.completed ?? items.filter((s) => s.learnerStatus === 'COMPLETED').length,
        };

        cachedSimulations = items;
        cachedStats = computedStats;
        if (user?.userId) cachedUserId = user.userId;
        setAllSimulations(items);
        setStats(computedStats);
      } else {
        // Fallback to me/simulations
        const simRes = await simulationsApi.list({ limit: 100 });
        if (simRes.success && simRes.data) {
          const simData = simRes.data as any;
          const items: SimulationItem[] = simData.simulations || [];

          const computedStats: DashboardStats = {
            total: simData.counts?.all ?? items.length,
            notStarted: simData.counts?.notStarted ?? items.filter((s) => s.learnerStatus === 'NOT_STARTED').length,
            inProgress: simData.counts?.inProgress ?? items.filter((s) => s.learnerStatus === 'IN_PROGRESS').length,
            completed: simData.counts?.completed ?? items.filter((s) => s.learnerStatus === 'COMPLETED').length,
          };

          cachedSimulations = items;
          cachedStats = computedStats;
          if (user?.userId) cachedUserId = user.userId;
          setAllSimulations(items);
          setStats(computedStats);
        } else {
          if (!cachedSimulations) {
            setError(res.error?.message ?? 'Failed to load dashboard simulations.');
          }
        }
      }
    } catch (err: any) {
      if (!cachedSimulations) {
        setError(err?.message || 'An error occurred while loading dashboard.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [user?.userId]);

  // Initial load on mount
  useEffect(() => {
    loadDashboardData(Boolean(cachedSimulations));
  }, [loadDashboardData]);

  // Detect user change (logout/login as another user or relogin)
  useEffect(() => {
    if (user?.userId && cachedUserId !== user.userId) {
      cachedUserId = user.userId;
      cachedSimulations = null;
      cachedStats = null;
      setAllSimulations([]);
      setIsLoading(true);
      loadDashboardData(false);
    }
  }, [user?.userId, loadDashboardData]);

  // Screen switch / Tab focus / Visibility change listener (auto-fetch newly assigned simulations)
  const lastAutoRefreshRef = useRef<number>(0);
  useEffect(() => {
    const triggerAutoRefresh = () => {
      const now = Date.now();
      // AUD-FE-001: Throttle window focus/visibility refreshes to at most once every 4 seconds
      if (now - lastAutoRefreshRef.current < 4000) return;
      lastAutoRefreshRef.current = now;
      loadDashboardData(true);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        triggerAutoRefresh();
      }
    };
    const handleFocus = () => {
      triggerAutoRefresh();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [loadDashboardData]);

  // Instant in-memory filtering by tab and search
  const filteredSimulations = useMemo(() => {
    let result = allSimulations;

    if (selectedTab !== 'ALL') {
      result = result.filter((s) => s.learnerStatus === selectedTab);
    }

    if (debouncedSearch.trim()) {
      const q = debouncedSearch.toLowerCase().trim();
      result = result.filter(
        (s) =>
          s.title.toLowerCase().includes(q) ||
          (s.description && s.description.toLowerCase().includes(q)) ||
          (s.category && s.category.toLowerCase().includes(q)) ||
          (s.targetPersona && s.targetPersona.toLowerCase().includes(q))
      );
    }

    return [...result].sort((a, b) => {
      if (sortBy === 'title') {
        return a.title.localeCompare(b.title);
      }
      if (sortBy === 'timeLimit') {
        return (a.timeLimitMinutes || 0) - (b.timeLimitMinutes || 0);
      }
      return 0;
    });
  }, [allSimulations, selectedTab, debouncedSearch, sortBy]);

  const {
    currentPage: simPage,
    pageSize: simPageSize,
    totalItems: simTotalItems,
    paginatedItems: paginatedSimulations,
    goToPage: setSimPage,
    setPageSize: setSimPageSize,
  } = usePagination(filteredSimulations, 1, 20);

  return (
    <div className="dashboard-page" style={{ display: 'grid', gap: '20px', maxWidth: '100%', minWidth: 0, overflowX: 'hidden' }}>
      {/* Header section with unified dark hero banner & breadcrumbs */}
      <AdminPageHeader
        breadcrumbs={[
          { label: 'Home', path: '/dashboard' },
          { label: 'Assigned Simulations' },
        ]}
        icon="🎯"
        title="Assigned Simulations & Practice Studio"
        badge="Roleplay Hub"
        description={`Select an assessment scenario to begin or continue your conversational roleplay • ${user?.email}`}
        actions={
          <button
            type="button"
            onClick={() => navigate('/attempts')}
            className="admin-btn admin-btn--secondary"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              height: '38px',
              padding: '0 14px',
              background: 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.15s ease',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            {t('nav.my_attempts', undefined, 'View My Attempts')}
          </button>
        }
      />

      {/* KPI Stats Summary Cards */}
      <div
        className="dashboard-page__cards"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(140px, 100%), 1fr))', gap: 'clamp(10px, 2vw, 16px)', maxWidth: '100%', minWidth: 0 }}
      >
        <div
          className="dashboard-card"
          onClick={() => setSelectedTab('ALL')}
          role="button"
          tabIndex={0}
          style={{
            background: 'var(--white)',
            border: selectedTab === 'ALL' ? '2px solid var(--blue, #2563eb)' : '1px solid var(--line)',
            borderRadius: '12px',
            padding: '16px 18px',
            display: 'flex',
            gap: '14px',
            alignItems: 'center',
            cursor: 'pointer',
            boxShadow: selectedTab === 'ALL' ? '0 2px 8px rgba(37,99,235,0.12)' : 'none',
            transition: 'all 0.15s ease',
            minWidth: 0,
          }}
        >
          <div
            className="icon-badge"
            style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'var(--blue-soft)', color: 'var(--blue)', display: 'grid', placeItems: 'center', fontSize: '18px', flexShrink: 0 }}
          >
            🎯
          </div>
          <div style={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '.04em', lineHeight: 1.25 }}>{t('dashboard.stats.total_simulations', undefined, 'Total Simulations')}</div>
            <div style={{ fontFamily: 'var(--display)', fontSize: '22px', fontWeight: 700, color: 'var(--ink)', marginTop: '2px' }}>{stats.total}</div>
          </div>
        </div>

        <div
          className="dashboard-card"
          onClick={() => setSelectedTab('IN_PROGRESS')}
          role="button"
          tabIndex={0}
          style={{
            background: 'var(--white)',
            border: selectedTab === 'IN_PROGRESS' ? '2px solid var(--warn, #d97706)' : '1px solid var(--line)',
            borderRadius: '12px',
            padding: '16px 18px',
            display: 'flex',
            gap: '14px',
            alignItems: 'center',
            cursor: 'pointer',
            boxShadow: selectedTab === 'IN_PROGRESS' ? '0 2px 8px rgba(217,119,6,0.12)' : 'none',
            transition: 'all 0.15s ease',
            minWidth: 0,
          }}
        >
          <div
            className="icon-badge warn"
            style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'var(--warn-soft)', color: 'var(--warn)', display: 'grid', placeItems: 'center', fontSize: '18px', flexShrink: 0 }}
          >
            ⏳
          </div>
          <div style={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '.04em', lineHeight: 1.25 }}>{t('dashboard.stats.in_progress', undefined, 'In Progress')}</div>
            <div style={{ fontFamily: 'var(--display)', fontSize: '22px', fontWeight: 700, color: 'var(--warn)', marginTop: '2px' }}>{stats.inProgress}</div>
          </div>
        </div>

        <div
          className="dashboard-card"
          onClick={() => setSelectedTab('COMPLETED')}
          role="button"
          tabIndex={0}
          style={{
            background: 'var(--white)',
            border: selectedTab === 'COMPLETED' ? '2px solid var(--ok, #16a34a)' : '1px solid var(--line)',
            borderRadius: '12px',
            padding: '16px 18px',
            display: 'flex',
            gap: '14px',
            alignItems: 'center',
            cursor: 'pointer',
            boxShadow: selectedTab === 'COMPLETED' ? '0 2px 8px rgba(22,163,74,0.12)' : 'none',
            transition: 'all 0.15s ease',
            minWidth: 0,
          }}
        >
          <div
            className="icon-badge ok"
            style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'var(--ok-soft)', color: 'var(--ok)', display: 'grid', placeItems: 'center', fontSize: '18px', flexShrink: 0 }}
          >
            ✅
          </div>
          <div style={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '.04em', lineHeight: 1.25 }}>{t('dashboard.stats.completed', undefined, 'Completed')}</div>
            <div style={{ fontFamily: 'var(--display)', fontSize: '22px', fontWeight: 700, color: 'var(--ok)', marginTop: '2px' }}>{stats.completed}</div>
          </div>
        </div>

        <div
          className="dashboard-card"
          onClick={() => setSelectedTab('NOT_STARTED')}
          role="button"
          tabIndex={0}
          style={{
            background: 'var(--white)',
            border: selectedTab === 'NOT_STARTED' ? '2px solid var(--muted, #64748b)' : '1px solid var(--line)',
            borderRadius: '12px',
            padding: '16px 18px',
            display: 'flex',
            gap: '14px',
            alignItems: 'center',
            cursor: 'pointer',
            boxShadow: selectedTab === 'NOT_STARTED' ? '0 2px 8px rgba(100,116,139,0.12)' : 'none',
            transition: 'all 0.15s ease',
            minWidth: 0,
          }}
        >
          <div
            className="icon-badge"
            style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'var(--surface)', color: 'var(--muted)', display: 'grid', placeItems: 'center', fontSize: '18px', flexShrink: 0 }}
          >
            🚀
          </div>
          <div style={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '.04em', lineHeight: 1.25 }}>{t('dashboard.stats.not_started', undefined, 'Not Started')}</div>
            <div style={{ fontFamily: 'var(--display)', fontSize: '22px', fontWeight: 700, color: 'var(--muted)', marginTop: '2px' }}>{stats.notStarted}</div>
          </div>
        </div>
      </div>

      {/* Competency Readiness & 90-Day Decay Health (PRD §12.1) */}
      <CompetencyReadinessCard
        baselineScore={86.5}
        onRefreshAssessment={() => setSelectedTab('NOT_STARTED')}
      />

      {/* Main Content Area: Filter toolbar and Simulation Cards */}
      <div className="dashboard-simulations-section" style={{ display: 'grid', gap: '16px', maxWidth: '100%', minWidth: 0 }}>
        <div
          className="dashboard-toolbar"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            flexWrap: 'wrap',
            borderBottom: '1px solid var(--line)',
            paddingBottom: '14px',
            maxWidth: '100%',
            minWidth: 0,
          }}
        >
          {/* Scrollable tab pills row */}
          <div
            className="tabs dashboard-tabs-scroll"
            role="tablist"
            aria-label="Simulation filter by status"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              overflowX: 'auto',
              scrollbarWidth: 'none',
              WebkitOverflowScrolling: 'touch',
              flexShrink: 0,
              paddingBottom: '2px',
              maxWidth: '100%',
              minWidth: 0,
            }}
          >
            {[
              { key: 'ALL' as const, label: t('table.filter_all', undefined, 'All'), count: stats.total },
              { key: 'NOT_STARTED' as const, label: t('table.filter_not_started', undefined, 'Not Started'), count: stats.notStarted },
              { key: 'IN_PROGRESS' as const, label: t('table.filter_in_progress', undefined, 'In Progress'), count: stats.inProgress },
              { key: 'COMPLETED' as const, label: t('table.filter_completed', undefined, 'Completed'), count: stats.completed },
            ].map((tab) => {
              const isActive = selectedTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`dashboard-tab-pill ${isActive ? 'active' : ''}`}
                  onClick={() => setSelectedTab(tab.key)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '7px 14px',
                    borderRadius: '8px',
                    fontSize: '13.5px',
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? 'var(--blue)' : 'var(--muted)',
                    background: isActive ? 'var(--blue-soft)' : 'var(--white)',
                    border: `1px solid ${isActive ? 'var(--blue)' : 'var(--line)'}`,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span>{tab.label}</span>
                  <span
                    className="c"
                    style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      padding: '1px 7px',
                      borderRadius: '999px',
                      background: isActive ? 'var(--blue)' : 'var(--surface)',
                      color: isActive ? '#ffffff' : 'var(--muted)',
                      lineHeight: '1.4',
                      display: 'inline-block',
                    }}
                  >
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Search + Sort tools — stack on mobile */}
          <div className="tools dashboard-tools-row" style={{ display: 'flex', gap: '10px', alignItems: 'center', flex: 1, minWidth: 0, flexWrap: 'wrap', maxWidth: '100%' }}>
            <div className="search dashboard-search-box" style={{ position: 'relative', flex: '1 1 180px', minWidth: 0, maxWidth: '100%' }}>
              <input
                type="text"
                placeholder={t('dashboard.search_placeholder', undefined, 'Search scenarios, personas...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: '100%', height: '40px', border: '1px solid var(--line)', borderRadius: '9px', padding: '0 12px 0 36px', background: 'var(--white)', fontSize: '13.5px' }}
              />
              <svg style={{ position: 'absolute', left: '12px', top: '12px', width: '16px', height: '16px', color: 'var(--faint)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '11px',
                    background: 'none',
                    border: 'none',
                    color: 'var(--faint)',
                    cursor: 'pointer',
                    fontSize: '14px',
                    padding: 0,
                  }}
                >
                  ✕
                </button>
              )}
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'createdAt' | 'title' | 'timeLimit')}
              className="sel"
              style={{ height: '40px', border: '1px solid var(--line)', borderRadius: '9px', padding: '0 32px 0 12px', background: 'var(--white)', fontSize: '13.5px', flexShrink: 0 }}
            >
              <option value="createdAt">{t('table.sort_latest', undefined, 'Sort: Latest')}</option>
              <option value="title">{t('table.sort_title', undefined, 'Sort: Title')}</option>
              <option value="timeLimit">{t('table.sort_duration', undefined, 'Sort: Duration')}</option>
            </select>
            <div className="views dashboard-view-toggle" style={{ display: 'flex', border: '1px solid var(--line)', borderRadius: '9px', overflow: 'hidden', background: 'var(--white)' }}>
              <button
                type="button"
                aria-pressed={viewMode === 'grid'}
                onClick={() => setViewMode('grid')}
                title="Grid View"
                style={{ width: '38px', height: '38px', border: 0, background: viewMode === 'grid' ? 'var(--blue-soft)' : 'none', color: viewMode === 'grid' ? 'var(--blue)' : 'var(--faint)', cursor: 'pointer', display: 'grid', placeItems: 'center' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                </svg>
              </button>
              <button
                type="button"
                aria-pressed={viewMode === 'list'}
                onClick={() => setViewMode('list')}
                title="List View"
                style={{ width: '38px', height: '38px', border: 0, background: viewMode === 'list' ? 'var(--blue-soft)' : 'none', color: viewMode === 'list' ? 'var(--blue)' : 'var(--faint)', cursor: 'pointer', display: 'grid', placeItems: 'center' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="8" y1="6" x2="21" y2="6" />
                  <line x1="8" y1="12" x2="21" y2="12" />
                  <line x1="8" y1="18" x2="21" y2="18" />
                  <line x1="3" y1="6" x2="3.01" y2="6" />
                  <line x1="3" y1="12" x2="3.01" y2="12" />
                  <line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Loading initial state */}
        {/* Loading initial state */}
        {isLoading && allSimulations.length === 0 && (
          <div
            style={{
              background: 'var(--white)',
              border: '1px solid var(--line)',
              borderRadius: '16px',
              padding: '60px 24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              minHeight: '380px',
              boxShadow: '0 1px 3px rgba(7, 29, 69, 0.04)',
            }}
          >
            <div
              className="dashboard-spinner"
              style={{ width: '40px', height: '40px', borderWidth: '3px', borderColor: '#2563eb', borderTopColor: 'transparent' }}
            />
            <div style={{ textAlign: 'center' }}>
              <h4 style={{ margin: '0 0 6px', fontSize: '16px', fontWeight: 700, color: 'var(--ink)' }}>
                Loading Assigned Simulations...
              </h4>
              <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--muted)' }}>
                Retrieving assigned assessment scenarios, progress status, and attempt limits.
              </p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="empty" style={{ background: 'var(--bad-soft)', border: '1px solid #F1C7C7', borderRadius: '14px', padding: '30px 20px', textAlign: 'center', color: 'var(--bad)' }}>
            <p>{error}</p>
            <button onClick={() => loadDashboardData(false)} className="btn primary" style={{ width: 'auto', padding: '0 18px', height: '38px', fontSize: '13.5px', marginTop: '10px' }}>
              Retry
            </button>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && filteredSimulations.length === 0 && (
          <div className="empty" style={{ background: 'var(--white)', border: '1px dashed #C3CEE0', borderRadius: '14px', padding: '40px 20px', textAlign: 'center', color: 'var(--muted)' }}>
            <div style={{ fontSize: '28px', marginBottom: '8px' }}>📂</div>
            <h3 style={{ fontFamily: 'var(--display)', fontWeight: 500, color: 'var(--ink)', fontSize: '18px', margin: 0 }}>No simulations found</h3>
            <p style={{ margin: '6px 0 0' }}>
              {searchQuery
                ? 'Try adjusting your search criteria.'
                : 'No simulations are currently assigned for this category.'}
            </p>
          </div>
        )}

        {/* Simulations Grid View */}
        {!error && filteredSimulations.length > 0 && viewMode === 'grid' && (
          <div className="sgrid">
            {paginatedSimulations.map((sim) => (
              <div key={sim.id} className="sim-card">
                <div className="sc-body">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span className="role" style={{ background: 'var(--blue-soft)', color: 'var(--blue)', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '6px', margin: 0 }}>
                        {sim.category || 'General'}
                      </span>
                      {sim.isAssigned && (
                        <span style={{ background: 'var(--ok-soft, #E4F5EE)', color: 'var(--ok, #0F8A5F)', border: '1px solid rgba(15, 138, 95, 0.2)', fontSize: '10.5px', fontWeight: 700, padding: '1px 6px', borderRadius: '6px', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                          <span>★</span> {t('status.assigned', undefined, 'Assigned')}
                        </span>
                      )}
                    </div>
                    <span className={`stat ${sim.learnerStatus === 'NOT_STARTED' ? 's-new' : sim.learnerStatus === 'IN_PROGRESS' ? 's-inp' : 's-done'}`}>
                      {sim.learnerStatus === 'NOT_STARTED'
                        ? t('status.not_started', undefined, 'Not Started')
                        : sim.learnerStatus === 'IN_PROGRESS'
                        ? t('status.in_progress', undefined, 'In Progress')
                        : t('status.completed', undefined, 'Completed')}
                    </span>
                  </div>

                  <h3 style={{ fontFamily: 'var(--display)', fontWeight: 600, fontSize: '17px', color: 'var(--ink)', margin: 0, minHeight: 'auto' }}>
                    {sim.title}
                  </h3>
                  <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--muted)', lineHeight: '1.45', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {sim.description}
                  </p>

                  <dl className="facts">
                    <div>
                      <dt>{t('dashboard.facts.time_limit', undefined, 'Time Limit')}</dt>
                      <dd>{sim.timeLimitMinutes || 15} <small>{t('dashboard.facts.mins', undefined, 'mins')}</small></dd>
                    </div>
                    <div>
                      <dt>{t('dashboard.facts.attempts', undefined, 'Attempts')}</dt>
                      <dd className="att">{sim.attemptsRemaining ?? (sim.maxAttempts || 3)} <em>/ {sim.maxAttempts || 3}</em></dd>
                    </div>
                    <div>
                      <dt>{t('dashboard.facts.pass_benchmark', undefined, 'Pass Benchmark')}</dt>
                      <dd>{(sim as any).passScore ? `${(sim as any).passScore}%` : '70%'}</dd>
                    </div>
                  </dl>

                  <div className="prog">
                    {(() => {
                      const progressPercent = typeof sim.progressPercentage === 'number'
                        ? sim.progressPercentage
                        : (sim.learnerStatus === 'COMPLETED'
                          ? 100
                          : (sim.learnerStatus === 'IN_PROGRESS'
                            ? Math.min(100, Math.round(((sim.minutesConsumed || 0) / Math.max(1, sim.totalDurationMinutes || sim.timeLimitMinutes || 10)) * 100))
                            : 0));
                      return (
                        <>
                          <div className="r">
                            <span>{t('dashboard.facts.attempt_progress', undefined, 'Attempt progress')}</span>
                            <b>{progressPercent}%</b>
                          </div>
                          <div className="pbar">
                            <i style={{ width: `${progressPercent}%` }} className={progressPercent === 100 ? 'ok' : ''}></i>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                <div className="sc-cta">
                  {sim.availableAction === 'START' && (
                    <button
                      onClick={() => navigate(`/simulations/${sim.id}/briefing`)}
                      className="btn primary md"
                    >
                      {t('dashboard.actions.briefing_and_start', undefined, 'Briefing & Start →')}
                    </button>
                  )}
                  {sim.availableAction === 'RESUME' && (
                    <button
                      onClick={() => {
                        const attemptParam = sim.activeAttemptId || sim.targetAttemptId;
                        const url = attemptParam
                          ? `/simulations/${sim.id}/run?attemptId=${attemptParam}`
                          : `/simulations/${sim.id}/briefing`;
                        navigate(url);
                      }}
                      className="btn primary md"
                      style={{ background: 'var(--warn)' }}
                    >
                      {t('dashboard.actions.resume_attempt', undefined, 'Resume Attempt →')}
                    </button>
                  )}
                  {sim.availableAction === 'VIEW_RESULTS' && (
                    <button
                      onClick={() => {
                        const targetId = sim.targetAttemptId || sim.activeAttemptId || sim.id;
                        navigate(`/attempts/${targetId}/results`);
                      }}
                      className="btn ghost md"
                    >
                      {t('dashboard.actions.view_scorecard', undefined, 'View Scorecard')}
                    </button>
                  )}
                  {sim.availableAction === 'MAX_ATTEMPTS_REACHED' && (
                    <button disabled className="btn off md">
                      {t('dashboard.actions.max_attempts_reached', undefined, 'Max Attempts Reached')}
                    </button>
                  )}
                  {sim.availableAction === 'LOCKED_FUTURE' && (
                    <button disabled className="btn off md">
                      {t('dashboard.actions.available_soon', undefined, 'Available Soon')}
                    </button>
                  )}
                  {sim.availableAction === 'EXPIRED' && (
                    <button disabled className="btn off md">
                      {t('dashboard.actions.simulation_closed', undefined, 'Simulation Closed')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Simulations Table List View */}
        {!error && filteredSimulations.length > 0 && viewMode === 'list' && (
          <div className="slist">
            <table>
              <thead>
                <tr>
                  <th>{t('table.simulation', undefined, 'Simulation')}</th>
                  <th>{t('table.category', undefined, 'Category')}</th>
                  <th>{t('table.status', undefined, 'Status')}</th>
                  <th>{t('table.time_limit', undefined, 'Time Limit')}</th>
                  <th>{t('table.attempts_left', undefined, 'Attempts Left')}</th>
                  <th>{t('table.progress', undefined, 'Progress')}</th>
                  <th>{t('table.actions', undefined, 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedSimulations.map((sim) => (
                  <tr key={sim.id}>
                    <td className="title">{sim.title}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span className="role">{sim.category || 'General'}</span>
                        {sim.isAssigned && (
                          <span style={{ background: 'var(--ok-soft, #E4F5EE)', color: 'var(--ok, #0F8A5F)', border: '1px solid rgba(15, 138, 95, 0.2)', fontSize: '10px', fontWeight: 700, padding: '1px 5px', borderRadius: '4px' }}>
                            ★ {t('status.assigned', undefined, 'Assigned')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`stat ${sim.learnerStatus === 'NOT_STARTED' ? 's-new' : sim.learnerStatus === 'IN_PROGRESS' ? 's-inp' : 's-done'}`}>
                        {sim.learnerStatus === 'NOT_STARTED'
                          ? t('status.not_started', undefined, 'Not Started')
                          : sim.learnerStatus === 'IN_PROGRESS'
                          ? t('status.in_progress', undefined, 'In Progress')
                          : t('status.completed', undefined, 'Completed')}
                      </span>
                    </td>
                    <td className="dt"><b>{sim.timeLimitMinutes || 15} {t('dashboard.facts.mins', undefined, 'mins')}</b></td>
                    <td className="att">{sim.attemptsRemaining ?? (sim.maxAttempts || 3)} <em>/ {sim.maxAttempts || 3}</em></td>
                    <td className="pg">
                      {(() => {
                        const progressPercent = typeof sim.progressPercentage === 'number'
                          ? sim.progressPercentage
                          : (sim.learnerStatus === 'COMPLETED'
                            ? 100
                            : (sim.learnerStatus === 'IN_PROGRESS'
                              ? Math.min(100, Math.round(((sim.minutesConsumed || 0) / Math.max(1, sim.totalDurationMinutes || sim.timeLimitMinutes || 10)) * 100))
                              : 0));
                        return (
                          <>
                            <div className="r">{progressPercent}%</div>
                            <div className="pbar">
                              <i style={{ width: `${progressPercent}%` }} className={progressPercent === 100 ? 'ok' : ''}></i>
                            </div>
                          </>
                        );
                      })()}
                    </td>
                    <td className="acts">
                      <div className="wrapb">
                        {sim.availableAction === 'START' && (
                          <button onClick={() => navigate(`/simulations/${sim.id}/briefing`)} className="btn primary md">
                            {t('dashboard.actions.briefing', undefined, 'Briefing →')}
                          </button>
                        )}
                        {sim.availableAction === 'RESUME' && (
                          <button
                            onClick={() => {
                              const attemptParam = sim.activeAttemptId || sim.targetAttemptId;
                              const url = attemptParam
                                ? `/simulations/${sim.id}/run?attemptId=${attemptParam}`
                                : `/simulations/${sim.id}/briefing`;
                              navigate(url);
                            }}
                            className="btn primary md"
                            style={{ background: 'var(--warn)' }}
                          >
                            {t('dashboard.actions.resume', undefined, 'Resume')}
                          </button>
                        )}
                        {sim.availableAction === 'VIEW_RESULTS' && (
                          <button onClick={() => navigate(`/attempts/${sim.targetAttemptId || sim.id}/results`)} className="btn ghost md">
                            {t('dashboard.actions.scorecard', undefined, 'Scorecard')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Bar */}
        {simTotalItems > 0 && (
          <div style={{ marginTop: '4px', background: 'var(--white)', borderRadius: '12px', border: '1px solid var(--line)', overflow: 'hidden' }}>
            <Pagination
              currentPage={simPage}
              totalItems={simTotalItems}
              pageSize={simPageSize}
              onPageChange={setSimPage}
              onPageSizeChange={setSimPageSize}
              showPageSizeSelector
              pageSizeOptions={[10, 20, 50, 100]}
              itemLabel="simulations"
            />
          </div>
        )}
      </div>

      {/* System Pre-Flight Modal */}
      <SystemCheckModal
        isOpen={Boolean(selectedSimForPreflight)}
        scenarioTitle={selectedSimForPreflight?.title}
        requiresMicrophone={selectedSimForPreflight?.requiresMicrophone ?? selectedSimForPreflight?.hardwareRequirements?.requiresMicrophone ?? false}
        requiresCamera={selectedSimForPreflight?.requiresCamera ?? selectedSimForPreflight?.hardwareRequirements?.requiresCamera ?? false}
        requiresSpeaker={selectedSimForPreflight?.requiresSpeaker ?? selectedSimForPreflight?.hardwareRequirements?.requiresSpeaker ?? false}
        onCancel={() => setSelectedSimForPreflight(null)}
        onComplete={() => {
          const sim = selectedSimForPreflight;
          setSelectedSimForPreflight(null);
          if (sim) {
            navigate(`/simulations/${sim.id}/briefing`);
          }
        }}
      />

      {/* Mobile FAB — Start next available simulation (hidden on desktop or if disabled by Admin) */}
      {branding?.mobileNavConfig?.enableMobileFab !== false && filteredSimulations.length > 0 && (
        <DashboardFAB
          label="Start Simulation"
          onClick={() => {
            const next = filteredSimulations.find(s => s.learnerStatus === 'NOT_STARTED' && s.isAvailable)
              || filteredSimulations.find(s => s.learnerStatus === 'IN_PROGRESS');
            if (next) {
              document.getElementById(`sim-card-${next.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }}
        />
      )}
    </div>
  );
}

